import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col, Label, Row } from "reactstrap";
import { useAnalyticsCore } from "../../cores/analytics";
import { linkedInBusinessApiService } from "../../cores/linkedInBusiness/services/api";
import { useLinkedInBusinessCore } from "../../cores/linkedInBusiness";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { LinkedInBusinessAnalyticsDashboard } from "../component/analytics/LinkedInBusinessAnalyticsDashboard";
import { TikTokAnalyticsDateRangeFilter } from "../component/analytics/TikTokAnalyticsDateRangeFilter";
import { PanelWembii } from "../component/PanelWembii";
import { LinkedInBusinessAnalyticsSnapshot } from "../component/analytics/linkedinBusinessAnalyticsTypes";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import _ from "lodash";
import "../component/analytics/LinkedInBusinessAnalytics.scss";

const toIsoRange = (since: string, until: string) => {
  const parse = (v: string) =>
    moment(v, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  const s = parse(since);
  const u = parse(until);
  if (!s.isValid() || !u.isValid()) return { since, until };
  return {
    since: s.format("YYYY-MM-DD"),
    until: u.format("YYYY-MM-DD"),
  };
};

export const LinkedInBusinessAnalytics = () => {
  const intl = useIntl();
  const { goToBinding, goToSocial } = useRouterCore();
  const { date } = useAnalyticsCore();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
    socialSettings,
  } = useMeCore();
  const { settings: linkedInBusinessSettings } = useLinkedInBusinessCore();

  const isSync = useMemo(
    () =>
      Boolean(
        socialSettings?.linkedInBusiness?.token?.length ||
          !_.isEmpty(linkedInBusinessSettings)
      ),
    [socialSettings?.linkedInBusiness, linkedInBusinessSettings]
  );

  const [snapshot, setSnapshot] = useState<LinkedInBusinessAnalyticsSnapshot | null>(
    null
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    ) {
      goToSocial();
    }
  }, [goToSocial, companyPlan, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    if (!isSync || !company?.id) return;
    const { since, until } = toIsoRange(date.since, date.until);
    setLoading(true);
    setLoadError(null);
    linkedInBusinessApiService
      .getInsights(String(company.id), since, until)
      .then((data) => setSnapshot(data))
      .catch((err: Error & { response?: { data?: { message?: string } } }) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          intl.formatMessage({ id: "app.linkedinBusinessAnalytics.loadError" });
        setLoadError(typeof msg === "string" ? msg : "Error");
        setSnapshot(null);
      })
      .finally(() => setLoading(false));
  }, [company?.id, date.since, date.until, isSync, intl]);

  return (
    <Layout>
      <Content>
        <div className="li-analytics-page-header">
          <div className="li-analytics-page-header__toolbar">
            <div className="li-analytics-page-header__lead">
              <FontAwesomeIcon icon={["fab", "linkedin"]} color="#0077B5" size="lg" />
              <h1 className="li-analytics-page-header__title">
                {intl.formatMessage({ id: "app.linkedinBusinessAnalytics.title" })}
              </h1>
            </div>
            {isSync ? (
              <div className="li-analytics-page-header__right">
                <TikTokAnalyticsDateRangeFilter />
              </div>
            ) : null}
          </div>
        </div>

        {!isSync ? (
          <Row className="d-flex justify-content-center">
            <Col xs="12" md="8" lg="6">
              <PanelWembii>
                <div className="li-analytics-unsynced">
                  <div className="li-analytics-unsynced__icon">
                    <FontAwesomeIcon icon={["fab", "linkedin"]} size="3x" />
                  </div>
                  <h3>
                    {intl.formatMessage({ id: "app.linkedinBusinessAnalytics.unsyncedTitle" })}
                  </h3>
                  <p className="li-analytics-muted mb-3">
                    {intl.formatMessage({ id: "app.linkedinBusinessAnalytics.unsyncedBody" })}
                  </p>
                  <Button color="primary" onClick={() => goToBinding()}>
                    {intl.formatMessage({ id: "app.home.syncUp" })}
                  </Button>
                </div>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}

        {isSync && loading ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <div className="d-flex justify-content-center py-4 text-muted">
                  {intl.formatMessage({ id: "app.reports.updating" })}
                </div>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}

        {isSync && loadError ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <Label className="text-muted text-center mb-0 w-100 py-3">
                  {loadError}
                </Label>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}

        {isSync && !loading && !loadError && snapshot ? (
          <LinkedInBusinessAnalyticsDashboard snapshot={snapshot} />
        ) : null}
      </Content>
    </Layout>
  );
};
